import { MAX_PRICE, MIN_PRICE } from '@/components/vehicle-search-filters/constants';
import { Field, FieldDescription } from '@/components/ui/field';
import { RangeSlider } from '@/components/range-slider';
import { DiscoveryFieldTitle } from '@/components/discovery-questions-input/field-title';
import { Box, type SxProps } from '@mui/material';
import { cn } from '@/lib/utils';

const formatPrice = (value: number) =>
  value.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

type PriceRangeSliderProps = {
  minPrice: number;
  maxPrice: number;
  setMinPrice?: (minPrice: number) => void;
  setMaxPrice?: (maxPrice: number) => void;
  onChangeCommitted?: (value: [number, number]) => void;
  title?: React.ReactNode;
  className?: string;
  sx?: SxProps;
};

export const PriceRangeSlider = ({
  minPrice,
  maxPrice,
  setMinPrice,
  setMaxPrice,
  onChangeCommitted,
  title,
  className,
  sx,
}: PriceRangeSliderProps) => {
  const showMaxSignature = maxPrice === MAX_PRICE;
  const maxPriceForDisplay = showMaxSignature ? `${formatPrice(maxPrice)}+` : formatPrice(maxPrice);

  const handleChangeCommitted = (value: [number, number]) => {
    if (onChangeCommitted) {
      onChangeCommitted(value);
      return;
    }
    setMinPrice?.(value[0]);
    setMaxPrice?.(value[1]);
  };

  return (
    // @TODO: Replace with div when all PriceRangeSlider instances are migrated from MUI
    <Box className={cn('w-full', className)} sx={sx}>
      <Field>
        <div className="flex w-full flex-row justify-between">
          {title ?? <DiscoveryFieldTitle>Set your budget range</DiscoveryFieldTitle>}
          <FieldDescription className="font-bold text-base text-left text-greyscale-1000 mb-4.5 md:text-lg tabular-nums">
            {formatPrice(minPrice)} to {maxPriceForDisplay}
          </FieldDescription>
        </div>
        <RangeSlider
          name="budget"
          min={MIN_PRICE}
          max={MAX_PRICE}
          value={[minPrice, maxPrice]}
          onChangeCommitted={handleChangeCommitted}
          step={1000}
        />
      </Field>
    </Box>
  );
};
